import type { StorageProvider } from "./persistence";
import { sGetRaw, sSetRaw } from "./scopedStorage";
import type { ProjectState, UserPreferences } from "../types";

// ── localStorage Keys ──
const LS_KEY = "openestimate_v2";
const LS_FILES_KEY = "openestimate_files_v2";
const LS_NOTES_KEY = "oe_history_notes_v1";
const LS_SHORTCUTS_KEY = "oe_shortcuts_v1";
const LS_TYPOLOGY_PREFS_KEY = "oe_typology_prefs_v1";

const DEFAULT_SHORTCUTS = { enterAddRow: true, ctrlZUndo: true, questionShortcuts: true };
const DEFAULT_TYPOLOGY_PREFS = {
  sfr:        { overhead: 10, profit: 8,  contingency: 5, divisions: ["div1","div2","div3","div4","div5","div6","div7","div8","div9","div13","div22","div23","div26","div32"] },
  multifamily:{ overhead: 12, profit: 9,  contingency: 5, divisions: ["div1","div2","div3","div4","div5","div6","div7","div8","div9","div22","div23","div26","div32"] },
  commercial: { overhead: 14, profit: 10, contingency: 7, divisions: ["div1","div2","div3","div4","div5","div6","div7","div8","div9","div10","div11","div22","div23","div25","div26","div27","div28","div31","div32"] },
  custom:     { overhead: 10, profit: 8,  contingency: 5, divisions: ["div1","div2","div3","div5","div6","div7","div8","div9","div22","div23","div26"] },
};

// ── Helpers ──
function lsGet<T>(key: string, fallback: T): T {
  try {
    const s = sGetRaw(key);
    return s ? JSON.parse(s) : fallback;
  } catch { return fallback; }
}

function lsSet(key: string, val: unknown): void {
  try { sSetRaw(key, JSON.stringify(val)); } catch {}
}

// ── LocalStorageProvider ──
export class LocalStorageProvider implements StorageProvider {
  loadCurrentProject(): ProjectState | null {
    return lsGet<ProjectState | null>(LS_KEY, null);
  }

  saveCurrentProject(state: ProjectState): void {
    lsSet(LS_KEY, state);
  }

  listProjects(): Record<string, ProjectState & { savedAt: string }> {
    return lsGet(LS_FILES_KEY, {});
  }

  saveProject(name: string, state: ProjectState): void {
    const files = this.listProjects();
    files[name] = { ...state, savedAt: new Date().toISOString() };
    lsSet(LS_FILES_KEY, files);
  }

  deleteProject(name: string): void {
    const files = this.listProjects();
    delete files[name];
    lsSet(LS_FILES_KEY, files);
  }

  loadPreferences(): UserPreferences {
    return {
      shortcuts: lsGet(LS_SHORTCUTS_KEY, DEFAULT_SHORTCUTS),
      typologyPrefs: lsGet(LS_TYPOLOGY_PREFS_KEY, DEFAULT_TYPOLOGY_PREFS),
      columnVisibility: {},
    };
  }

  saveShortcuts(shortcuts: UserPreferences["shortcuts"]): void {
    lsSet(LS_SHORTCUTS_KEY, shortcuts);
  }

  saveTypologyPrefs(prefs: UserPreferences["typologyPrefs"]): void {
    lsSet(LS_TYPOLOGY_PREFS_KEY, prefs);
  }

  loadHistoryNotes(): Record<string, string> {
    return lsGet(LS_NOTES_KEY, {});
  }

  saveHistoryNotes(notes: Record<string, string>): void {
    lsSet(LS_NOTES_KEY, notes);
  }

  getCurrentUser() {
    return null; // No auth in localStorage mode
  }

  isAuthenticated() {
    return false;
  }
}

// Singleton for convenience
export const localStorageProvider = new LocalStorageProvider();
